import axios from 'axios'

const WAIT_LIKE_FOREVER = 10000000
const PROD_WAIT = 90000

import { closeModal, showModal } from 'actions/app'

import { Alert, Box, Button } from '@mui/material'
import i18next from 'i18next'
import { clearIntegrationData } from 'common/integrationUtils'
import { getErrorMessageForAlert } from 'common/http-error'
import { TEST_IDS } from 'test-ids'

function DismissibleAlertModal({ message }) {
  const handleClose = () => {
    import('store')
      .then((storeModule) => {
        storeModule.default.dispatch(closeModal())
      })
      .catch((error) => {
        console.error('Failed to lazy-load store for error modal', error)
      })
  }

  return (
    <Box>
      <Alert severity="error">{message}</Alert>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleClose}
          data-testid={TEST_IDS.modalCloseButton}
        >
          {i18next.t('close')}
        </Button>
      </Box>
    </Box>
  )
}

const AlertComponent = function (errorResponse) {
  const errorMessage =
    getErrorMessageForAlert(errorResponse) || i18next.t('generic_error')

  // Lazy import to avoid circular dependency during initialization
  import('store')
    .then((storeModule) => {
      const store = storeModule.default
      store.dispatch(
        showModal(<DismissibleAlertModal message={errorMessage} />, {
          title: i18next.t('alert'),
        }),
      )
    })
    .catch((error) => {
      console.error('Failed to lazy-load store for error modal', error)
    })
}

/*
 * `showModal` has a single slot, so the global alert replaces whatever dialog is
 * open — including one that wants to stay up and let the user correct the
 * request. Callers that render the failure themselves pass
 * `{ suppressErrorModal: true }` as request config and handle the rejection.
 *
 * The rejection is unchanged either way; this only governs the global modal.
 */
const rejectAndAlert = (config) => (err) => {
  if (!config?.suppressErrorModal) AlertComponent(err.response?.data)
  return Promise.reject(err)
}

class Http {
  constructor() {
    this._defaultProps = {}
    this._client = undefined
  }

  init(props) {
    const { baseURL = 'http://localhost' } = props
    const { logout } = props.auth0

    this._defaultProps = {
      baseURL,
      timeout: IS_PRODUCTION ? PROD_WAIT : WAIT_LIKE_FOREVER,
    }

    this._client = axios.create(this._defaultProps)

    this._client.interceptors.response.use(
      function (response) {
        return response
      },
      function (error) {
        const status = error?.response?.status
        const redirectUrl = error?.response?.data?.redirectUrl
        if (status === 421 && redirectUrl) {
          //load provided app, auth seems to persist
          //window.location.href = redirectUrl
          return Promise.resolve(error.response)
        } else if (error?.response?.data === 'jwt expired') {
          clearIntegrationData()
          //Remove the CFF localStorage items
          localStorage.removeItem('selectedClient')
          localStorage.removeItem('selectedFarms')
          //End
          logout({ logoutParams: { returnTo: window.location.origin } })
        } else {
          return Promise.reject(error)
        }
      },
    )
  }

  withTokens(accessToken) {
    if (!accessToken) throw new Error('access token required!')

    this._client.defaults.headers.common['Authorization'] =
      'Bearer ' + accessToken
    this._client.defaults.headers.common['Accept-Language'] = navigator.language
  }

  get(url, config) {
    return this._client.get(url, config).catch(rejectAndAlert(config))
  }

  post(url, payload, config) {
    return this._client.post(url, payload, config).catch(rejectAndAlert(config))
  }

  put(url, payload, config) {
    return this._client.put(url, payload, config).catch(rejectAndAlert(config))
  }

  delete(url, config) {
    return this._client.delete(url, config).catch(rejectAndAlert(config))
  }
}

export default new Http()
